import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "@app/components/shared/Card";
import { Button } from "@app/components/shared/Button";
import { SectionHeader } from "@app/components/shared/SectionHeader";
import { Pill } from "@app/components/shared/Pill";
import {
  listPlans,
  updatePlanLimit,
  listPlanPrices,
  createOrUpdatePlanPrice,
  type Plan,
  type UpdatePlanLimitRequest,
  type PlanPrice,
  type CreateOrUpdatePlanPriceRequest,
} from "@app/services/adminPlansApi";
import { logger } from "@app/services/logger";
import "./plans.css";

function EditCapModal({
  plan,
  onClose,
  onSuccess,
}: {
  plan: Plan;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [dailyTradeCap, setDailyTradeCap] = useState(
    plan.limit?.daily_trade_cap ?? 12,
  );
  const updateLimitMutation = useMutation({
    mutationFn: (data: UpdatePlanLimitRequest) =>
      updatePlanLimit(plan.id, data),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (error) => {
      logger.error("Failed to update plan limit:", error instanceof Error ? error : new Error(String(error)));
      alert(`Failed to update plan limit: ${error.message}`);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateLimitMutation.mutate({ daily_trade_cap: dailyTradeCap });
  };

  return (
    <div className="modal-overlay">
      <Card title={`Edit ${plan.name} Daily Trade Cap`} className="modal-card">
        <form onSubmit={handleSubmit} className="edit-cap-form">
          <label>
            Daily Trade Cap:
            <input
              type="number"
              min="0"
              value={dailyTradeCap}
              onChange={(e) => setDailyTradeCap(parseInt(e.target.value) || 0)}
              required
            />
            <small>Number of new entry executions allowed per day</small>
          </label>
          <div className="modal-actions">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={updateLimitMutation.isPending}
            >
              {updateLimitMutation.isPending ? "Updating..." : "Update Cap"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

function PlanPricesCell({ planId }: { planId: string }) {
  const { data: prices, isLoading } = useQuery<PlanPrice[]>({
    queryKey: ["plan-prices", planId],
    queryFn: () => listPlanPrices(planId),
  });

  if (isLoading) {
    return (
      <>
        <td>Loading...</td>
        <td>Loading...</td>
      </>
    );
  }

  const monthly = prices?.find((p) => p.period === "monthly");
  const yearly = prices?.find((p) => p.period === "yearly");

  return (
    <>
      <td>
        {monthly ? `$${monthly.price_usd.toFixed(2)}` : "N/A"}
      </td>
      <td>
        {yearly ? `$${yearly.price_usd.toFixed(2)}` : "N/A"}
      </td>
    </>
  );
}

function EditPricesModal({
  plan,
  onClose,
  onSuccess,
}: {
  plan: Plan;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const queryClient = useQueryClient();
  const [monthlyPrice, setMonthlyPrice] = useState<number | null>(null);
  const [yearlyPrice, setYearlyPrice] = useState<number | null>(null);
  const [yearlyPriceOverride, setYearlyPriceOverride] = useState<number | null>(null);
  const [useAutoCalculate, setUseAutoCalculate] = useState(true);

  const YEARLY_DISCOUNT_PERCENTAGE = 15;

  const { data: prices, isLoading: isLoadingPrices, error: pricesError } = useQuery<PlanPrice[]>({
    queryKey: ["plan-prices", plan.id],
    queryFn: () => listPlanPrices(plan.id),
  });

  useEffect(() => {
    if (prices) {
      const monthly = prices.find((p) => p.period === "monthly");
      const yearly = prices.find((p) => p.period === "yearly");
      setMonthlyPrice(monthly ? monthly.price_usd : null);
      const loadedYearly = yearly ? yearly.price_usd : null;
      setYearlyPriceOverride(loadedYearly);

      if (monthly && loadedYearly) {
        const autoCalculated = (monthly.price_usd * 12) * (1 - YEARLY_DISCOUNT_PERCENTAGE / 100);
        setUseAutoCalculate(Math.abs(loadedYearly - autoCalculated) < 0.01);
      }
    }
  }, [prices]);

  useEffect(() => {
    if (useAutoCalculate && monthlyPrice !== null && monthlyPrice >= 0) {
      const yearlyWithoutDiscount = monthlyPrice * 12;
      const calculated = yearlyWithoutDiscount * (1 - YEARLY_DISCOUNT_PERCENTAGE / 100);
      setYearlyPrice(calculated);
    } else if (yearlyPriceOverride !== null) {
      setYearlyPrice(yearlyPriceOverride);
    }
  }, [monthlyPrice, useAutoCalculate, yearlyPriceOverride]);

  const updatePriceMutation = useMutation({
    mutationFn: (data: CreateOrUpdatePlanPriceRequest) =>
      createOrUpdatePlanPrice(plan.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plan-prices", plan.id] });
      queryClient.invalidateQueries({ queryKey: ["plans"] });
    },
    onError: (error) => {
      logger.error("Failed to update plan price:", error instanceof Error ? error : new Error(String(error)));
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (monthlyPrice !== null && monthlyPrice >= 0) {
        await updatePriceMutation.mutateAsync({
          period: "monthly",
          price_usd: monthlyPrice,
        });
      }

      if (yearlyPrice !== null && yearlyPrice >= 0) {
        await updatePriceMutation.mutateAsync({
          period: "yearly",
          price_usd: yearlyPrice,
        });
      }

      onSuccess();
      onClose();
    } catch (error: unknown) {
      const err = error instanceof Error ? error : new Error(String(error));
      logger.error("Failed to update plan prices:", err);

      const errorMessage = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        || err.message
        || "Unknown error occurred";
      alert(`Failed to update plan prices: ${errorMessage}`);
    }
  };

  if (isLoadingPrices) {
    return (
      <div className="modal-overlay">
        <Card title={`Edit ${plan.name} Prices`} className="modal-card">
          <div>Loading prices...</div>
        </Card>
      </div>
    );
  }

  if (pricesError) {
    return (
      <div className="modal-overlay">
        <Card title={`Edit ${plan.name} Prices`} className="modal-card">
          <div style={{ color: "red" }}>
            Error loading prices: {pricesError instanceof Error ? pricesError.message : "Unknown error"}
          </div>
          <div className="modal-actions" style={{ marginTop: "1rem" }}>
            <Button type="button" variant="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <Card title={`Edit ${plan.name} Prices`} className="modal-card">
        <form onSubmit={handleSubmit} className="edit-cap-form">
          <label>
            Monthly Price (USD):
            <input
              type="number"
              min="0"
              step="0.01"
              value={monthlyPrice ?? ""}
              onChange={(e) =>
                setMonthlyPrice(e.target.value ? parseFloat(e.target.value) : null)
              }
              placeholder="Enter monthly price"
              required
            />
            <small>Price per month in USD</small>
          </label>
          <label>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
              <input
                type="checkbox"
                checked={useAutoCalculate}
                onChange={(e) => {
                  setUseAutoCalculate(e.target.checked);
                  if (e.target.checked && monthlyPrice !== null) {
                    const yearlyWithoutDiscount = monthlyPrice * 12;
                    const calculated = yearlyWithoutDiscount * (1 - YEARLY_DISCOUNT_PERCENTAGE / 100);
                    setYearlyPrice(calculated);
                  }
                }}
              />
              <span>Auto-calculate yearly price ({YEARLY_DISCOUNT_PERCENTAGE}% discount)</span>
            </div>
            Yearly Price (USD):
            <input
              type="number"
              min="0"
              step="0.01"
              value={yearlyPrice ?? ""}
              onChange={(e) => {
                const value = e.target.value ? parseFloat(e.target.value) : null;
                setYearlyPrice(value);
                setYearlyPriceOverride(value);
                setUseAutoCalculate(false);
              }}
              placeholder={useAutoCalculate ? "Auto-calculated" : "Enter yearly price"}
              disabled={useAutoCalculate}
              required
            />
            <small>
              {useAutoCalculate && monthlyPrice !== null
                ? `Auto-calculated: $${(monthlyPrice * 12).toFixed(2)} x (1 - ${YEARLY_DISCOUNT_PERCENTAGE}%) = $${yearlyPrice?.toFixed(2) ?? "0.00"} (Save ${YEARLY_DISCOUNT_PERCENTAGE}%)`
                : "Price per year in USD (manual override)"
              }
            </small>
          </label>
          <div className="modal-actions">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={updatePriceMutation.isPending}
            >
              {updatePriceMutation.isPending ? "Updating..." : "Update Prices"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

export function PlansTab(): JSX.Element {
  const queryClient = useQueryClient();
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [editingPricesPlan, setEditingPricesPlan] = useState<Plan | null>(null);

  const {
    data: plans,
    isLoading,
    error,
  } = useQuery<Plan[]>({
    queryKey: ["plans"],
    queryFn: listPlans,
  });

  const handleEditCap = (plan: Plan) => {
    setEditingPlan(plan);
  };

  const handleEditPrices = (plan: Plan) => {
    setEditingPricesPlan(plan);
  };

  if (isLoading) {
    return <div>Loading plans...</div>;
  }

  if (error) {
    return <div>Error loading plans: {error.message}</div>;
  }

  return (
    <div className="plans-root">
      <SectionHeader
        title="Plans Management"
        subtitle="Manage trading plans, prices, and daily trade caps. Standard flow and higher flow options available."
      />

      <Card className="plans-list-card">
        {plans && plans.length > 0 ? (
          <table className="plans-table">
            <thead>
              <tr>
                <th>Plan Code</th>
                <th>Plan Name</th>
                <th>Status</th>
                <th>Monthly Price</th>
                <th>Yearly Price</th>
                <th>Daily Trade Cap</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {plans.map((plan) => (
                <tr key={plan.id}>
                  <td>
                    <strong>{plan.code}</strong>
                  </td>
                  <td>{plan.name}</td>
                  <td>
                    <Pill tone={plan.is_active ? "success" : "danger"}>
                      {plan.is_active ? "Active" : "Inactive"}
                    </Pill>
                  </td>
                  <PlanPricesCell planId={plan.id} />
                  <td>
                    {plan.limit ? (
                      <span className="cap-value">{plan.limit.daily_trade_cap}</span>
                    ) : (
                      <span className="cap-value">Not configured</span>
                    )}
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                      <Button
                        variant="secondary"
                        onClick={() => handleEditPrices(plan)}
                        size="small"
                      >
                        Edit Prices
                      </Button>
                      <Button
                        variant="primary"
                        onClick={() => handleEditCap(plan)}
                        size="small"
                      >
                        Edit Cap
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="plans-empty">No plans found.</div>
        )}
      </Card>

      {editingPlan && (
        <EditCapModal
          plan={editingPlan}
          onClose={() => setEditingPlan(null)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["plans"] });
            setEditingPlan(null);
          }}
        />
      )}

      {editingPricesPlan && (
        <EditPricesModal
          plan={editingPricesPlan}
          onClose={() => setEditingPricesPlan(null)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["plans"] });
            queryClient.invalidateQueries({ queryKey: ["plan-prices"] });
            setEditingPricesPlan(null);
          }}
        />
      )}
    </div>
  );
}
